import { useEffect, useMemo, useState } from "react";
import { Printer, FileText } from "lucide-react";
import { api, brl } from "@/lib/api";

const todayStr = () => new Date().toISOString().slice(0, 10);

const presets = {
  hoje: () => [todayStr(), todayStr()],
  semana: () => {
    const d = new Date();
    const start = new Date(d);
    start.setDate(d.getDate() - d.getDay());
    return [start.toISOString().slice(0, 10), todayStr()];
  },
  mes: () => {
    const d = new Date();
    return [`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`, todayStr()];
  },
};

export default function Relatorios() {
  const [start, setStart] = useState(presets.mes()[0]);
  const [end, setEnd] = useState(presets.mes()[1]);
  const [data, setData] = useState({ rows: [], total: 0, count: 0, average: 0 });
  const [page, setPage] = useState(1);
  const PER_PAGE = 50;

  useEffect(() => {
    if (!start || !end) return;
    api.get("/reports/sales", { params: { start, end } }).then((r) => setData(r.data)).catch(() => {});
  }, [start, end]);

  useEffect(() => {
    setPage(1);
  }, [start, end, data]);

  const totalPages = Math.max(1, Math.ceil(data.rows.length / PER_PAGE));
  const pageRows = data.rows.slice((page - 1) * PER_PAGE, page * PER_PAGE);
  const printChunks = [];
  for (let i = 0; i < data.rows.length; i += 50) printChunks.push(data.rows.slice(i, i + 50));

  const periodLabel = useMemo(
    () => `${new Date(start + "T12:00:00").toLocaleDateString("pt-BR")} até ${new Date(end + "T12:00:00").toLocaleDateString("pt-BR")}`,
    [start, end]
  );

  return (
    <div data-testid="relatorios-page">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6 no-print">
        <h1 className="font-display text-3xl sm:text-4xl font-light">Relatório de Vendas</h1>
        <div className="flex flex-wrap items-center gap-2">
          {Object.entries({ hoje: "Hoje", semana: "Esta semana", mes: "Este mês" }).map(([key, label]) => (
            <button
              key={key}
              onClick={() => {
                const [s, e] = presets[key]();
                setStart(s);
                setEnd(e);
              }}
              className="px-4 py-2 rounded-full text-xs border border-stone-300 text-stone-600 hover:border-stone-900 hover:text-stone-900 transition-colors"
              data-testid={`relatorios-preset-${key}`}
            >
              {label}
            </button>
          ))}
          <input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="rounded-full border border-stone-300 bg-white px-4 py-2 text-sm focus:outline-none focus:border-[#C8A279]" data-testid="relatorios-start-date" />
          <span className="text-stone-400 text-sm">até</span>
          <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="rounded-full border border-stone-300 bg-white px-4 py-2 text-sm focus:outline-none focus:border-[#C8A279]" data-testid="relatorios-end-date" />
          <button onClick={() => window.print()} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#121212] text-white text-sm font-medium hover:bg-stone-700 transition-colors" data-testid="relatorios-print-button">
            <Printer size={15} /> Imprimir / PDF
          </button>
        </div>
      </div>

      <div className="grid sm:grid-cols-3 gap-4 mb-6 no-print">
        <div className="bg-white border border-stone-200 rounded-2xl p-6 shadow-[0_2px_12px_rgba(0,0,0,0.03)]">
          <p className="text-xs font-semibold tracking-[0.15em] uppercase text-stone-500 mb-2">Faturamento total</p>
          <p className="font-display text-3xl" data-testid="relatorios-total">{brl(data.total)}</p>
        </div>
        <div className="bg-white border border-stone-200 rounded-2xl p-6 shadow-[0_2px_12px_rgba(0,0,0,0.03)]">
          <p className="text-xs font-semibold tracking-[0.15em] uppercase text-stone-500 mb-2">Atendimentos</p>
          <p className="font-display text-3xl" data-testid="relatorios-count">{data.count}</p>
        </div>
        <div className="bg-white border border-stone-200 rounded-2xl p-6 shadow-[0_2px_12px_rgba(0,0,0,0.03)]">
          <p className="text-xs font-semibold tracking-[0.15em] uppercase text-stone-500 mb-2">Ticket médio</p>
          <p className="font-display text-3xl" data-testid="relatorios-average">{brl(data.average)}</p>
        </div>
      </div>

      <div className="bg-white border border-stone-200 rounded-2xl p-5 sm:p-8 shadow-[0_2px_12px_rgba(0,0,0,0.03)] print-area">
        <div className="mb-6">
          <p className="text-[10px] font-semibold tracking-[0.2em] uppercase text-[#C8A279]">Marcio Pissaia — Cabeleireiro Unissex</p>
          <h2 className="font-display text-2xl mt-1">Histórico de Vendas</h2>
          <p className="text-sm text-stone-500">{periodLabel}</p>
        </div>
        <div className="overflow-x-auto screen-only">
          <table className="w-full text-sm" data-testid="relatorios-table">
            <thead>
              <tr className="border-b-2 border-stone-200 text-left text-xs font-semibold tracking-[0.12em] uppercase text-stone-500">
                <th className="py-3 pr-4">Data</th>
                <th className="py-3 pr-4">Cliente</th>
                <th className="py-3 pr-4">Serviço</th>
                <th className="py-3 pr-4 text-right">Valor</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.length === 0 && (
                <tr>
                  <td colSpan={4} className="py-12 text-center text-stone-400">
                    <FileText size={28} className="mx-auto mb-3 text-stone-300" />
                    Nenhuma venda no período selecionado.
                  </td>
                </tr>
              )}
              {pageRows.map((r, i) => (
                <tr key={i} className="border-b border-stone-100 hover:bg-stone-50 transition-colors" data-testid={`relatorios-row-${(page - 1) * PER_PAGE + i}`}>
                  <td className="py-3 pr-4 text-stone-600 whitespace-nowrap">{new Date(r.date + "T12:00:00").toLocaleDateString("pt-BR")}</td>
                  <td className="py-3 pr-4 font-medium whitespace-nowrap overflow-hidden text-ellipsis max-w-[180px]">{r.client_name}</td>
                  <td className="py-3 pr-4 text-stone-600 whitespace-nowrap overflow-hidden text-ellipsis max-w-[340px]" title={r.services}>{r.services}</td>
                  <td className="py-3 pr-4 text-right font-semibold whitespace-nowrap" data-testid={`relatorios-value-${(page - 1) * PER_PAGE + i}`}>{brl(r.value)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              {data.rows.length > 0 && (
                <tr className="border-t-2 border-stone-300 font-semibold">
                  <td className="py-3.5 pr-4" colSpan={2}>
                    Atendimentos: <span data-testid="relatorios-footer-count">{data.count}</span>
                  </td>
                  <td className="py-3.5 pr-4 text-right text-stone-600">Soma</td>
                  <td className="py-3.5 text-right" data-testid="relatorios-footer-soma">{brl(data.total)}</td>
                </tr>
              )}
            </tfoot>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-5 no-print" data-testid="relatorios-pagination">
            <p className="text-sm text-stone-500">
              Página {page} de {totalPages} · {data.rows.length} registros · 50 por página
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-4 py-2 rounded-full border border-stone-300 text-sm text-stone-600 hover:border-stone-900 transition-colors disabled:opacity-40"
                data-testid="relatorios-prev-page"
              >
                Anterior
              </button>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="px-4 py-2 rounded-full border border-stone-300 text-sm text-stone-600 hover:border-stone-900 transition-colors disabled:opacity-40"
                data-testid="relatorios-next-page"
              >
                Próxima
              </button>
            </div>
          </div>
        )}

        {/* Versão para impressão: blocos de 50 lançamentos por folha */}
        <div className="print-only" data-testid="relatorios-print-table">
          {printChunks.map((chunk, ci) => {
            const last = ci === printChunks.length - 1;
            const somaChunk = chunk.reduce((s, r) => s + r.value, 0);
            return (
              <div key={ci} className={last ? "" : "print-chunk"}>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b-2 border-stone-300 text-left text-[9px] font-semibold tracking-[0.08em] uppercase text-stone-500">
                      <th className="py-1 pr-3">Data</th>
                      <th className="py-1 pr-3">Cliente</th>
                      <th className="py-1 pr-3">Serviço</th>
                      <th className="py-1 pr-3 text-right">Valor</th>
                    </tr>
                  </thead>
                  <tbody>
                    {chunk.map((r, i) => (
                      <tr key={i} className="border-b border-stone-200">
                        <td className="py-[1px] pr-3 text-[10px] leading-[12px] text-stone-600 whitespace-nowrap">{new Date(r.date + "T12:00:00").toLocaleDateString("pt-BR")}</td>
                        <td className="py-[1px] pr-3 text-[10px] leading-[12px] font-medium whitespace-nowrap overflow-hidden text-ellipsis max-w-[180px]">{r.client_name}</td>
                        <td className="py-[1px] pr-3 text-[10px] leading-[12px] text-stone-600 whitespace-nowrap overflow-hidden text-ellipsis max-w-[340px]">{r.services}</td>
                        <td className="py-[1px] pr-3 text-[10px] leading-[12px] text-right font-semibold whitespace-nowrap">{brl(r.value)}</td>
                      </tr>
                    ))}
                  </tbody>
                  {last && (
                    <tfoot>
                      <tr className="border-t-2 border-stone-300 font-semibold text-[11px]">
                        <td className="py-1.5 pr-3" colSpan={2}>Atendimentos: {data.count}</td>
                        <td className="py-1.5 pr-3 text-right text-stone-600">Soma</td>
                        <td className="py-1.5 text-right">{brl(data.total)}</td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            );
          })}
          {printChunks.length === 0 && (
            <table className="w-full text-sm">
              <tbody>
                <tr>
                  <td className="py-10 text-center text-stone-400">Nenhuma venda no período selecionado.</td>
                </tr>
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
